/**
 * Settings: AI configuration, widget customisation and conversation flow.
 *
 * Laid out as a left sub-nav of collapsible groups, mirroring LiveChatAI so
 * staff moving across find things where they expect. Each panel saves only its
 * own fields — see `useBotSettings` for why that matters.
 */

import { useState } from 'react';

import { api } from '../api/client';
import type { Bot, BotSettings, WidgetConfig } from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import {
  DEFAULT_BRANDING_TEXT,
  WidgetMockPreview,
  widgetColors,
  type PreviewView,
  type WidgetColors,
} from '../components/WidgetPreview';
import { useBotSettings } from '../hooks/useBotSettings';
import { useBots } from '../hooks/useBots';
import { botLabel } from '../lib/format';

type PanelId =
  | 'identity'
  | 'behaviour'
  | 'knowledge'
  | 'appearance'
  | 'preview-message'
  | 'fullpage'
  | 'language'
  | 'availability'
  | 'custom-css'
  | 'flow'
  | 'lead-form'
  | 'attachments'
  | 'notifications';

interface Group {
  label: string;
  panels: { id: PanelId; label: string }[];
}

const GROUPS: Group[] = [
  {
    label: 'AI Configuration',
    panels: [
      { id: 'identity', label: 'Identity' },
      { id: 'behaviour', label: 'Behaviour & Instructions' },
      { id: 'knowledge', label: 'Knowledge & Safety' },
    ],
  },
  {
    label: 'Widget Customization',
    panels: [
      { id: 'appearance', label: 'Style & Appearance' },
      { id: 'preview-message', label: 'Preview Message' },
      { id: 'fullpage', label: 'Full Page' },
      { id: 'language', label: 'Widget Language' },
      { id: 'availability', label: 'Availability Schedule' },
      { id: 'custom-css', label: 'Custom Styling' },
    ],
  },
  {
    label: 'Conversation Flow',
    panels: [{ id: 'flow', label: 'Initial & Suggested Messages' }],
  },
  {
    label: 'Lead Capture',
    panels: [
      { id: 'lead-form', label: 'Zip / Postal Code' },
      { id: 'attachments', label: 'File Uploads' },
    ],
  },
  {
    label: 'Reporting',
    panels: [{ id: 'notifications', label: 'Report Recipients' }],
  },
];

type ColorKey =
  | 'header_color'
  | 'header_text_color'
  | 'launcher_color'
  | 'background_color'
  | 'ai_message_color'
  | 'ai_text_color'
  | 'user_message_color'
  | 'user_text_color'
  | 'button_color'
  | 'button_text_color'
  | 'link_color';

interface ColorFieldSpec {
  key: ColorKey;
  label: string;
  /** Which drawn colour this field controls, for showing its current value. */
  shows: keyof WidgetColors;
  /** Shown while unset; absent for the three colours every bot always has. */
  defaultHint?: string;
}

/** Every colour the widget draws with, grouped by where it appears. */
const COLOR_GROUPS: { label: string; fields: ColorFieldSpec[] }[] = [
  {
    label: 'Header & launcher',
    fields: [
      { key: 'header_color', label: 'Header', shows: 'header' },
      {
        key: 'header_text_color',
        label: 'Header text & icon',
        shows: 'headerText',
        defaultHint: 'White',
      },
      {
        key: 'launcher_color',
        label: 'Launcher button',
        shows: 'launcher',
        defaultHint: 'Same as header',
      },
    ],
  },
  {
    label: 'Chat window',
    fields: [
      {
        key: 'background_color',
        label: 'Background',
        shows: 'background',
        defaultHint: 'White',
      },
      {
        key: 'link_color',
        label: 'Links in replies',
        shows: 'link',
        defaultHint: 'Same as visitor message',
      },
    ],
  },
  {
    label: 'Messages',
    fields: [
      { key: 'ai_message_color', label: 'AI message', shows: 'ai' },
      {
        key: 'ai_text_color',
        label: 'AI message text',
        shows: 'aiText',
        defaultHint: 'Near-black',
      },
      { key: 'user_message_color', label: 'Visitor message', shows: 'user' },
      {
        key: 'user_text_color',
        label: 'Visitor message text',
        shows: 'userText',
        defaultHint: 'White',
      },
    ],
  },
  {
    label: 'Buttons',
    fields: [
      {
        key: 'button_color',
        label: 'Send & suggestion buttons',
        shows: 'button',
        defaultHint: 'Same as visitor message',
      },
      {
        key: 'button_text_color',
        label: 'Send button text',
        shows: 'buttonText',
        defaultHint: 'White',
      },
    ],
  },
];

const INPUT =
  'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue';
const LABEL = 'block text-sm font-medium text-slate-700';

export function Settings(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;
  const state = useBotSettings(slug);
  const [panel, setPanel] = useState<PanelId>('identity');
  const [view, setView] = useState<PreviewView>('conversation');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (!slug) return <EmptyState title="No bot selected" />;
  if (state.loading) return <Spinner label="Loading settings…" />;
  if (!state.settings || !state.bot) {
    return <ErrorNotice message={state.error ?? 'This bot has no settings row.'} />;
  }

  const settings = state.settings;
  const widget = settings.widget;
  const colors = widgetColors(widget);
  const showsWidgetPreview = GROUPS[1].panels.some((item) => item.id === panel);

  const setWidget = (changes: Partial<WidgetConfig>): void =>
    state.patch({ widget: { ...widget, ...changes } });

  const saveWidget = (): Promise<void> => state.save({ widget });
  const saveFields = (fields: (keyof BotSettings)[]): Promise<void> =>
    state.save(
      Object.fromEntries(
        fields.map((field) => [field, settings[field]]),
      ) as Partial<BotSettings>,
    );

  return (
    <div>
      <PageHeader
        title="Settings"
        description={`Configuration for ${botLabel(state.bot)}`}
        actions={
          <span className="self-center text-xs text-slate-400">
            {state.saving
              ? 'Saving…'
              : state.savedAt
                ? 'All changes saved'
                : 'No unsaved changes'}
          </span>
        }
      />

      {state.error && <ErrorNotice message={state.error} />}

      <div className="grid gap-4 lg:grid-cols-[15rem_1fr]">
        <Card className="h-fit p-2">
          {GROUPS.map((group) => (
            <div key={group.label} className="mb-1">
              <button
                type="button"
                onClick={() =>
                  setCollapsed((current) => ({
                    ...current,
                    [group.label]: !current[group.label],
                  }))
                }
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50"
              >
                {group.label}
                <span>{collapsed[group.label] ? '▸' : '▾'}</span>
              </button>
              {!collapsed[group.label] && (
                <ul>
                  {group.panels.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setPanel(item.id)}
                        className={`w-full rounded px-3 py-1.5 text-left text-sm ${
                          panel === item.id
                            ? 'bg-brand-light font-medium text-brand-navy'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </Card>

        <div
          className={
            showsWidgetPreview ? 'grid gap-4 xl:grid-cols-[1fr_24rem]' : 'grid gap-4'
          }
        >
          <Card className="p-5">
            {panel === 'identity' && (
              <IdentityPanel slug={slug} bot={state.bot} onSaved={state.reload} />
            )}

            {panel === 'behaviour' && (
              <>
                <h2 className="mb-3 text-base font-semibold">
                  Behaviour &amp; Instructions
                </h2>
                <label className={LABEL} htmlFor="base-prompt">
                  Base prompt
                </label>
                <textarea
                  id="base-prompt"
                  rows={16}
                  value={settings.base_prompt}
                  onChange={(event) => state.patch({ base_prompt: event.target.value })}
                  className={`${INPUT} font-mono text-xs`}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {settings.base_prompt.length.toLocaleString()} characters. Retrieved
                  site content is appended automatically at answer time — do not paste
                  it here.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveFields(['base_prompt'])}>
                    Save prompt
                  </Button>
                </div>
              </>
            )}

            {panel === 'knowledge' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Knowledge &amp; Safety</h2>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={settings.restrict_to_data_sources}
                    onChange={(event) =>
                      state.patch({ restrict_to_data_sources: event.target.checked })
                    }
                  />
                  <span>
                    <strong>Answer only from this bot&apos;s data sources.</strong>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Strongly recommended. With this off, the assistant answers from
                      the model&apos;s general knowledge when your site has nothing
                      relevant — which is how a support bot ends up confidently
                      describing products you do not sell.
                    </span>
                  </span>
                </label>

                <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={settings.strict_grounding}
                    disabled={!settings.restrict_to_data_sources}
                    onChange={(event) =>
                      state.patch({ strict_grounding: event.target.checked })
                    }
                  />
                  <span>
                    <strong>Never answer without a source (enforced).</strong>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      When a message matches nothing in the knowledge base, the reply is
                      your fallback message and the AI model is never called at all — so
                      &ldquo;2x3=?&rdquo;, trivia and coding questions have no route to
                      an answer. Turn this off and refusing becomes an instruction the
                      model is asked to follow rather than a guarantee. Requires{' '}
                      <em>answer only from data sources</em>.
                    </span>
                  </span>
                </label>

                <label className={`${LABEL} mt-4`} htmlFor="fallback">
                  Fallback message
                </label>
                <textarea
                  id="fallback"
                  rows={3}
                  value={settings.fallback_message}
                  onChange={(event) =>
                    state.patch({ fallback_message: event.target.value })
                  }
                  className={INPUT}
                />
                <p className="mt-1 text-xs text-slate-500">
                  What the visitor sees when your data sources have nothing on their
                  question. With <em>never answer without a source</em> on, it is sent
                  word for word. With only <em>answer only from data sources</em> on,
                  the AI is asked to say something along these lines. With both off, it
                  isn&apos;t used. A good fallback points somewhere useful, such as a
                  phone number or a contact form.
                </p>

                <label className="mt-5 flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={settings.match_visitor_language}
                    onChange={(event) =>
                      state.patch({ match_visitor_language: event.target.checked })
                    }
                  />
                  <span>
                    <strong>Reply in the visitor&apos;s language.</strong>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      A visitor who writes in Spanish gets a Spanish answer, and so on
                      for any language. Your training content stays in English — the
                      assistant translates from it as it answers. With this off, replies
                      are always in English regardless of what the visitor wrote.
                    </span>
                  </span>
                </label>

                <label className={`${LABEL} mt-5`} htmlFor="synonyms">
                  Search synonyms
                </label>
                <textarea
                  id="synonyms"
                  rows={5}
                  value={settings.search_synonyms.join('\n')}
                  onChange={(event) =>
                    state.patch({
                      search_synonyms: event.target.value.split('\n'),
                    })
                  }
                  placeholder={'pegboard, peg board, drying rack\ndrip tray, drip trough'}
                  className={`${INPUT} font-mono text-xs`}
                />
                <p className="mt-1 text-xs text-slate-500">
                  One group per line, variants separated by commas. A question using
                  any variant is searched for all of them, so a visitor asking about a
                  &ldquo;drying rack&rdquo; finds the page that only says
                  &ldquo;pegboards&rdquo;. Single words also match their plurals;
                  phrases match exactly as written.
                </p>

                <div className="mt-4">
                  <Button
                    onClick={() =>
                      void saveFields([
                        'restrict_to_data_sources',
                        'strict_grounding',
                        'fallback_message',
                        'match_visitor_language',
                        'search_synonyms',
                      ])
                    }
                  >
                    Save knowledge settings
                  </Button>
                </div>
              </>
            )}

            {panel === 'notifications' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Report Recipients</h2>
                <label className={LABEL} htmlFor="recipients">
                  Email addresses, one per line
                </label>
                <textarea
                  id="recipients"
                  rows={4}
                  value={settings.report_recipients.join('\n')}
                  onChange={(event) =>
                    state.patch({
                      report_recipients: event.target.value
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="mktg@formaspace.com"
                  className={INPUT}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Where this bot&apos;s review reports are emailed. Leave empty to use
                  the default recipients set on the Email page.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveFields(['report_recipients'])}>
                    Save recipients
                  </Button>
                </div>
              </>
            )}

            {panel === 'appearance' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Style &amp; Appearance</h2>
                {COLOR_GROUPS.map((group) => (
                  <fieldset key={group.label} className="mb-4">
                    <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {group.label}
                    </legend>
                    <div className="grid gap-4 sm:grid-cols-3">
                      {group.fields.map((field) => (
                        <ColorField
                          key={field.key}
                          field={field}
                          value={widget[field.key]}
                          shown={colors[field.shows]}
                          onChange={(value) =>
                            setWidget({ [field.key]: value } as Partial<WidgetConfig>)
                          }
                        />
                      ))}
                    </div>
                  </fieldset>
                ))}

                <div className="mt-4">
                  <span className={LABEL}>Launcher position</span>
                  <div className="mt-1 flex gap-2">
                    {(['left', 'right'] as const).map((side) => (
                      <button
                        key={side}
                        type="button"
                        onClick={() => setWidget({ alignment: side })}
                        className={`rounded-md border px-3 py-1.5 text-sm capitalize ${
                          widget.alignment === side
                            ? 'border-brand-blue bg-brand-light text-brand-navy'
                            : 'border-slate-300 text-slate-600'
                        }`}
                      >
                        {side}
                      </button>
                    ))}
                  </div>
                </div>

                {IMAGE_FIELDS.map((field) => (
                  <ImageUpload
                    key={field.key}
                    field={field}
                    value={widget[field.key]}
                    onChange={(value) =>
                      setWidget({ [field.key]: value } as Partial<WidgetConfig>)
                    }
                  />
                ))}

                <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={widget.show_branding}
                    onChange={(event) =>
                      setWidget({ show_branding: event.target.checked })
                    }
                  />
                  Show the &ldquo;Powered by&rdquo; footer
                </label>

                {widget.show_branding && (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={LABEL} htmlFor="branding-text">
                        Footer text
                      </label>
                      <input
                        id="branding-text"
                        maxLength={80}
                        value={widget.branding_text ?? ''}
                        placeholder={DEFAULT_BRANDING_TEXT}
                        onChange={(event) =>
                          setWidget({ branding_text: event.target.value })
                        }
                        className={INPUT}
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        e.g. &ldquo;Powered by Workbenches.com&rdquo;. Blank restores
                        the default.
                      </p>
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="branding-url">
                        Footer link (optional)
                      </label>
                      <input
                        id="branding-url"
                        type="url"
                        value={widget.branding_url ?? ''}
                        placeholder="https://www.workbenches.com"
                        onChange={(event) =>
                          setWidget({ branding_url: event.target.value || null })
                        }
                        className={INPUT}
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        http:// or https:// only. Leave blank for plain text.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save appearance</Button>
                </div>
              </>
            )}

            {panel === 'lead-form' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Zip / Postal Code</h2>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={widget.require_postal_code !== false}
                    onChange={(event) =>
                      setWidget({ require_postal_code: event.target.checked })
                    }
                  />
                  Ask for a zip / postal code on the lead form
                </label>
                <p className="ml-6 mt-1 text-xs text-slate-500">
                  Leads are routed to a sales rep by zip code. Turn off for sites
                  whose leads are not routed that way. The visitor&rsquo;s zip code
                  shows in the Chat Inbox sidebar.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save</Button>
                </div>
              </>
            )}

            {panel === 'attachments' && (
              <>
                <h2 className="mb-3 text-base font-semibold">File Uploads</h2>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={widget.allow_attachments !== false}
                    onChange={(event) =>
                      setWidget({ allow_attachments: event.target.checked })
                    }
                  />
                  Let visitors send files from the chat
                </label>
                <p className="ml-6 mt-1 text-xs text-slate-500">
                  Photos, PDFs, Office documents, drawings and 3D models (DWG, DXF,
                  STEP, STL, IGES, OBJ, 3MF, SKP) and ZIP files, up to 10 per chat.
                  Files are stored in Azure Blob Storage, appear in the Chat Inbox,
                  and go to NetSuite with the lead. The paperclip only shows once
                  Azure storage is set up for the deployment; System check says
                  whether it is.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save</Button>
                </div>
              </>
            )}

            {panel === 'preview-message' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Preview Message</h2>
                <label className={LABEL} htmlFor="notice">
                  Dismissible notice
                </label>
                <input
                  id="notice"
                  value={widget.dismissible_notice ?? ''}
                  onChange={(event) =>
                    setWidget({ dismissible_notice: event.target.value })
                  }
                  className={INPUT}
                />
                <p className="mt-1 text-xs text-slate-500">
                  The bubble shown beside the launcher before the widget is opened.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save notice</Button>
                </div>
              </>
            )}

            {panel === 'fullpage' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Full Page</h2>
                <p className="text-sm text-slate-600">
                  A standalone chat page for this bot, for links in email signatures or
                  campaigns.
                </p>
                <code className="mt-3 block rounded bg-slate-50 p-3 text-xs text-slate-700">
                  {window.location.origin}/chat/{slug}
                </code>
                <p className="mt-2 text-xs text-slate-500">
                  Arrives with the Embed &amp; Integrate page in milestone 8.
                </p>
              </>
            )}

            {panel === 'language' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Widget Language</h2>
                <label className={LABEL} htmlFor="lang">
                  Interface language
                </label>
                <select
                  id="lang"
                  value={widget.language}
                  onChange={(event) => setWidget({ language: event.target.value })}
                  className={INPUT}
                >
                  {[
                    ['en', 'English'],
                    ['es', 'Español'],
                    ['fr', 'Français'],
                    ['de', 'Deutsch'],
                  ].map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save language</Button>
                </div>
              </>
            )}

            {panel === 'availability' && (
              <AvailabilityPanel
                widget={widget}
                onChange={setWidget}
                onSave={() => void saveWidget()}
              />
            )}

            {panel === 'custom-css' && (
              <>
                <h2 className="mb-3 text-base font-semibold">Custom Styling</h2>
                <label className={LABEL} htmlFor="css">
                  Custom CSS
                </label>
                <textarea
                  id="css"
                  rows={12}
                  value={widget.custom_css}
                  onChange={(event) => setWidget({ custom_css: event.target.value })}
                  className={`${INPUT} font-mono text-xs`}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Injected inside the widget&apos;s shadow root, so it cannot affect the
                  host page.
                </p>
                <div className="mt-4">
                  <Button onClick={() => void saveWidget()}>Save CSS</Button>
                </div>
              </>
            )}

            {panel === 'flow' && (
              <>
                <h2 className="mb-3 text-base font-semibold">
                  Initial &amp; Suggested Messages
                </h2>
                <label className={LABEL} htmlFor="greeting">
                  Greeting
                </label>
                <textarea
                  id="greeting"
                  rows={3}
                  value={settings.greeting_message}
                  onChange={(event) =>
                    state.patch({ greeting_message: event.target.value })
                  }
                  className={INPUT}
                />

                <span className={`${LABEL} mt-4 block`}>Suggested messages</span>
                {settings.suggested_messages.map((item, index) => (
                  <div key={index} className="mt-2 flex gap-2">
                    <input
                      value={item}
                      onChange={(event) => {
                        const next = [...settings.suggested_messages];
                        next[index] = event.target.value;
                        state.patch({ suggested_messages: next });
                      }}
                      className={INPUT}
                    />
                    <Button
                      variant="danger"
                      onClick={() =>
                        state.patch({
                          suggested_messages: settings.suggested_messages.filter(
                            (_, position) => position !== index,
                          ),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      state.patch({
                        suggested_messages: [...settings.suggested_messages, ''],
                      })
                    }
                  >
                    Add suggestion
                  </Button>
                </div>

                <div className="mt-4">
                  <Button
                    onClick={() =>
                      void saveFields(['greeting_message', 'suggested_messages'])
                    }
                  >
                    Save conversation flow
                  </Button>
                </div>
              </>
            )}
          </Card>

          {showsWidgetPreview && (
            <Card className="p-4">
              <div className="mb-3 flex gap-1">
                {(['conversation', 'teaser', 'fullpage'] as PreviewView[]).map(
                  (option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setView(option)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${
                        view === option
                          ? 'bg-brand-navy text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {option}
                    </button>
                  ),
                )}
              </div>
              <div className="rounded-lg bg-slate-100 p-3">
                <WidgetMockPreview
                  widget={widget}
                  botName={state.bot.name}
                  greeting={settings.greeting_message}
                  suggestions={settings.suggested_messages}
                  view={view}
                />
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                Updates as you type. The Preview tab runs the real widget.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function IdentityPanel({
  slug,
  bot,
  onSaved,
}: {
  slug: string;
  bot: Bot;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(bot.name);
  const [siteUrl, setSiteUrl] = useState(bot.site_url);
  const [origins, setOrigins] = useState(bot.allowed_origins.join('\n'));
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await api.updateBot(slug, {
        name,
        site_url: siteUrl,
        allowed_origins: origins
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      });
      await onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="mb-3 text-base font-semibold">Identity</h2>
      <label className={LABEL} htmlFor="bot-name">
        Display name
      </label>
      <input
        id="bot-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        className={INPUT}
      />

      <label className={`${LABEL} mt-4`} htmlFor="site-url">
        Website
      </label>
      <input
        id="site-url"
        value={siteUrl}
        onChange={(event) => setSiteUrl(event.target.value)}
        className={INPUT}
      />

      <label className={`${LABEL} mt-4`} htmlFor="origins">
        Allowed origins
      </label>
      <textarea
        id="origins"
        rows={4}
        value={origins}
        onChange={(event) => setOrigins(event.target.value)}
        placeholder="https://formaspace.com"
        className={`${INPUT} font-mono text-xs`}
      />
      <p className="mt-1 text-xs text-slate-500">
        One per line. Only these sites may load the widget or open a chat socket. Leave
        empty to allow any origin — acceptable while testing, not in production.
      </p>

      <div className="mt-4">
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save identity'}
        </Button>
      </div>
    </>
  );
}

interface ImageField {
  key: 'header_logo_url' | 'avatar_url' | 'brand_logo_url';
  label: string;
  hint: string;
  /** Largest width and height the upload is scaled down to, in pixels. */
  maxWidth: number;
  maxHeight: number;
}

const IMAGE_FIELDS: ImageField[] = [
  {
    key: 'header_logo_url',
    label: 'Header logo',
    hint: 'Shown in the chat window’s header bar, next to the bot’s name. A wide logo on a transparent background works best.',
    maxWidth: 480,
    maxHeight: 96,
  },
  {
    key: 'avatar_url',
    label: 'AI avatar',
    hint: 'A small round picture shown beside each of the assistant’s messages. A square image of a face or icon works best.',
    maxWidth: 128,
    maxHeight: 128,
  },
  {
    key: 'brand_logo_url',
    label: 'Brand logo',
    hint: 'Shown centred at the top of the conversation, above the greeting.',
    maxWidth: 480,
    maxHeight: 96,
  },
];

const IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';

/** Uploads larger than this are refused before they are even decoded. */
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * Shrink an uploaded image and return it as a PNG data URL.
 *
 * The result is stored in the widget config itself, which every visitor's
 * browser downloads, so it is kept to icon size rather than whatever the
 * original was. Redrawing through a canvas also flattens SVGs to pixels, so a
 * script embedded in an uploaded SVG never reaches a visitor.
 */
/**
 * One colour picker. The optional colours show their default until changed,
 * and "Reset" returns them to null, so they go back to following the colour
 * they default to.
 */
function ColorField({
  field,
  value,
  shown,
  onChange,
}: {
  field: ColorFieldSpec;
  value: string | null;
  shown: string;
  onChange: (value: string | null) => void;
}): JSX.Element {
  const id = `color-${field.key}`;
  const unset = field.defaultHint !== undefined && value === null;
  return (
    <div>
      <label className={LABEL} htmlFor={id}>
        {field.label}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={shown}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 w-12 cursor-pointer rounded border border-slate-300"
        />
        {unset ? (
          <span className="text-xs text-slate-400">{field.defaultHint}</span>
        ) : (
          <code className="text-xs text-slate-500">{shown}</code>
        )}
        {field.defaultHint !== undefined && !unset && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-slate-500 underline hover:text-slate-700"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

async function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<string> {
  const source = URL.createObjectURL(file);
  try {
    // `onload` rather than `image.decode()`: decode() rejects some valid SVGs in
    // Firefox and never settles in headless Chromium.
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('unreadable image'));
      element.src = source;
    });
    const width = image.naturalWidth || maxWidth;
    const height = image.naturalHeight || maxHeight;
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser cannot process images.');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(source);
  }
}

function ImageUpload({
  field,
  value,
  onChange,
}: {
  field: ImageField;
  value: WidgetConfig[ImageField['key']];
  onChange: (value: string | null) => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputId = `upload-${field.key}`;

  async function handleFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file: PNG, JPG, WebP, GIF or SVG.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('That image is over 5 MB. Choose a smaller one.');
      return;
    }
    setBusy(true);
    try {
      onChange(await resizeImage(file, field.maxWidth, field.maxHeight));
    } catch {
      setError('That image could not be read. Try saving it as a PNG first.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4">
      <span className={LABEL}>{field.label}</span>
      <div className="mt-1 flex items-center gap-3">
        <div className="flex h-14 w-28 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50">
          {value ? (
            <img src={value} alt={field.label} className="max-h-12 max-w-[6.5rem]" />
          ) : (
            <span className="text-xs text-slate-400">No image</span>
          )}
        </div>
        <label
          htmlFor={inputId}
          className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          {busy ? 'Processing…' : value ? 'Replace' : 'Upload image'}
        </label>
        <input
          id={inputId}
          type="file"
          accept={IMAGE_TYPES}
          className="sr-only"
          onChange={(event) => {
            void handleFile(event.target.files?.[0]);
            // Clear it so choosing the same file again still fires onChange.
            event.target.value = '';
          }}
        />
        {value && (
          <Button variant="danger" onClick={() => onChange(null)}>
            Remove
          </Button>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <p className="mt-1 text-xs text-slate-500">
        {field.hint} PNG, JPG, WebP, GIF or SVG; it is resized automatically. Click{' '}
        <em>Save appearance</em> to apply.
      </p>
    </div>
  );
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function AvailabilityPanel({
  widget,
  onChange,
  onSave,
}: {
  widget: WidgetConfig;
  onChange: (changes: Partial<WidgetConfig>) => void;
  onSave: () => void;
}): JSX.Element {
  const schedule = (widget.availability_schedule ?? {}) as {
    timezone?: string;
    days?: Record<string, [string, string] | null>;
  };
  const days = schedule.days ?? {};

  const setDay = (day: string, value: [string, string] | null): void =>
    onChange({
      availability_schedule: {
        ...schedule,
        timezone: schedule.timezone ?? 'America/Chicago',
        days: { ...days, [day]: value },
      },
    });

  return (
    <>
      <h2 className="mb-3 text-base font-semibold">Availability Schedule</h2>
      <label className={LABEL} htmlFor="tz">
        Timezone
      </label>
      <input
        id="tz"
        value={schedule.timezone ?? 'America/Chicago'}
        onChange={(event) =>
          onChange({
            availability_schedule: { ...schedule, timezone: event.target.value, days },
          })
        }
        className={INPUT}
      />

      <div className="mt-4 space-y-2">
        {DAYS.map((day) => {
          const hours = days[day];
          return (
            <div key={day} className="flex items-center gap-2">
              <label className="flex w-24 items-center gap-2 text-sm capitalize text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(hours)}
                  onChange={(event) =>
                    setDay(day, event.target.checked ? ['08:00', '18:00'] : null)
                  }
                />
                {day}
              </label>
              {hours && (
                <>
                  <input
                    type="time"
                    value={hours[0]}
                    onChange={(event) => setDay(day, [event.target.value, hours[1]])}
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="time"
                    value={hours[1]}
                    onChange={(event) => setDay(day, [hours[0], event.target.value])}
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Unchecked days hide the launcher entirely. An empty schedule means always
        available.
      </p>

      <div className="mt-4">
        <Button onClick={onSave}>Save schedule</Button>
      </div>
    </>
  );
}
